import { UrlParamType } from '@/modules/utils'
import { appendParamsIfNeeded } from '@/utils'
import { useRouter } from 'next/router'
import { useCallback, useEffect } from 'react'
import useFirstRouterQueryParam from './useFirstRouterQueryParam'
import useCurrentUser from '@/hooks/useCurrentUser'

const useIsQueryOwner = (readOnly = false) => {
  const router = useRouter()
  const [queryType] = useFirstRouterQueryParam(['type'])

  const { isPetSitter, isOwner, currentUser } = useCurrentUser()

  useEffect(() => {
    let finalType: UrlParamType | null = null

    // If user is a pet sitter and the query doesn't match, fix it.
    if (isPetSitter) {
      // If user is a pet sitter and the query doesn't match, fix it.
      if (queryType !== UrlParamType.Pro) {
        finalType = UrlParamType.Pro
      }
    } else {
      // Original logic for owner/borrower (now including UrlParamType.Pro)
      if (queryType !== UrlParamType.Owner && queryType !== UrlParamType.Borrower && queryType !== UrlParamType.Pro) {
        // If none of Owner/Borrower/Pro match, pick Owner or Borrower based on isOwner
        finalType = isOwner ? UrlParamType.Owner : UrlParamType.Borrower
      } else {
        if (queryType === UrlParamType.Owner && currentUser && !isOwner) {
          finalType = UrlParamType.Borrower
        } else if (queryType === UrlParamType.Borrower && currentUser && isOwner) {
          finalType = UrlParamType.Owner
        } else if (queryType === UrlParamType.Pro) {
          // Handle the Pro case explicitly
          finalType = UrlParamType.Pro
        }
      }
    }

    if (!readOnly && finalType && finalType !== queryType) {
      router.replace(appendParamsIfNeeded(router.asPath, { type: finalType }), undefined, { shallow: true })
    }
  }, [queryType, currentUser, isOwner, readOnly, isPetSitter])

  const appendQueryType = useCallback(
    (route: string, params?: Record<string, string>) => {
      return appendParamsIfNeeded(route, { type: queryType, ...params })
    },
    [queryType],
  )

  // TESTING
  // const isUrlOwner = true
  const isUrlOwner = queryType === UrlParamType.Owner

  return [isUrlOwner, queryType, appendQueryType] as const
}

export default useIsQueryOwner
